import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";

import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";
import FlashContext from "components/FlashMessages/FlashContext";
import { Download } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import CodeSnippetBlock from "ds/components/CodeSnippetBlock";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { downloadFileFromURL } from "utils/file";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";

import InstructionSteps from "../InstructionSteps";
import { GET_STACK_RUN_TRACE_DOWNLOAD_URL } from "./gql";

const JAEGER_STEPS = [
  "Open Jaeger UI and go to the Search page.",
  "Switch to the Upload tab.",
  "Drop in the downloaded file to open the run in the timeline view.",
];

const JAEGER_DEMO_URL = "https://jaeger.demo.jaegertracing.io/search";
const JAEGER_DOCKER_COMMAND = `docker run --rm --name jaeger \\
  -p 127.0.0.1:16686:16686 \\
  cr.jaegertracing.io/jaegertracing/jaeger:2.22.0`;

type ExportTracesDrawerProps = {
  stackId: string;
  runId: string;
  partial: boolean;
  source: string;
};

const ExportTracesDrawer = createDrawer((props: ExportTracesDrawerProps) => {
  const { stackId, runId, partial, source } = props;
  const { reportError } = useTypedContext(FlashContext);
  const trackSegmentAnalyticsEvent = useAnalytics();

  const title = partial ? "Export partial traces" : "Export traces";

  const description = partial
    ? "The trace downloads as a single Jaeger JSON file holding the spans captured so far."
    : "The trace downloads as a single Jaeger JSON file holding every span from this run.";

  const [getTraceDownloadURL, { loading }] = useLazyQuery(GET_STACK_RUN_TRACE_DOWNLOAD_URL, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const handleDownload = useCallback(async () => {
    let data, error;
    try {
      ({ data, error } = await getTraceDownloadURL({
        variables: { stackId, runId },
        context: { suppressFlashError: true },
      }));
    } catch (requestError) {
      rethrowIfNotAborted(requestError as Error);
      return;
    }

    if (error) {
      reportError({
        title: "Export failed",
        message: "Failed to export traces. Please try again later.",
      });
      return;
    }

    const url = data?.stack?.run?.otelTraceForJaegerDownloadURL?.url;

    if (!url) {
      reportError({
        message: "Traces are not available for this run.",
      });
      return;
    }

    downloadFileFromURL(url);

    trackSegmentAnalyticsEvent("Run Observability - export traces", { source });
  }, [getTraceDownloadURL, stackId, runId, source, reportError, trackSegmentAnalyticsEvent]);

  return (
    <DrawerSimple>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title={title} />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody fullHeight gap="x-large">
        <Banner variant="info">{description}</Banner>
        <Box direction="column" gap="large">
          <Typography tag="h3" variant="p-t7">
            Get a Jaeger instance
          </Typography>
          <Box direction="column" gap="x-large">
            <Box direction="column" gap="medium">
              <Typography tag="h4" variant="p-t8">
                Option 1: Use the hosted demo
              </Typography>
              <Typography tag="p" variant="p-body3">
                Open the{" "}
                <Link href={JAEGER_DEMO_URL} target="_blank" rel="noopener noreferrer" inheritFont>
                  Jaeger demo
                </Link>
                . It does not save uploaded traces.
              </Typography>
            </Box>
            <Box direction="column" gap="medium">
              <Typography tag="h4" variant="p-t8">
                Option 2: Run Jaeger locally
              </Typography>
              <InstructionSteps
                steps={[
                  "Install Docker if you don't have it.",
                  "Run this command to start Jaeger:",
                ]}
              />
              <CodeSnippetBlock language="shell" allowCopy copyTitle="Copy command">
                {JAEGER_DOCKER_COMMAND}
              </CodeSnippetBlock>
              <InstructionSteps start={3} steps={["Open localhost:16686 in your browser."]} />
            </Box>
          </Box>
        </Box>
        <Box direction="column" gap="large">
          <Typography tag="h3" variant="p-t7">
            Open it in Jaeger
          </Typography>
          <InstructionSteps steps={JAEGER_STEPS} />
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <DrawerFooterActions>
          <Button variant="primary" startIcon={Download} onPress={handleDownload} loading={loading}>
            Download trace
          </Button>
        </DrawerFooterActions>
      </DrawerFooter>
    </DrawerSimple>
  );
});

export const showExportTracesDrawer = createDrawerTrigger(ExportTracesDrawer);
